import React from "react";
import { Formik, FormikProps } from "formik";
import { JoiningPage } from "translations/src/models/joining-pages";
import { ActionButton, IDropzoneUploaderFile } from "component-library";
import { DeleteIcon } from "assets";

import { IAboutYouFormPhotoModel } from "../../../../../../formModels/AboutYouFormModel";
import HeadshotDropzone from "../HeadshotDropzone";
import { createUploadImageSchema } from "../UploadImageValidation";
import { useTranslations } from "../../../../../../hooks/useTranslations";
import { useApplication } from "../../../../../../context/ApplicationContext";
import { constructWebmediaPhotoUrl } from "../../../../../../helpers/url-helpers";
import UploadImageModal from "../UploadImageModal";

interface IAboutYouFormPart1Props {
  initialFormValues: IAboutYouFormPhotoModel;
  showUploadModal: boolean;
  setShowUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (values: IAboutYouFormPhotoModel) => void;
  clearHeadshotError: () => void;
  headshotFormError: boolean;
  submittingForm: boolean;
  submittingHeadshot: boolean;
  clearHeadshotFormData: () => void;
  headshotUploadError: boolean;
  isYP?: boolean;
}

const AboutYouFormPart1 = React.forwardRef<
  FormikProps<IAboutYouFormPhotoModel>,
  IAboutYouFormPart1Props
>((props, ref) => {
  const {
    initialFormValues,
    showUploadModal,
    setShowUploadModal,
    onSubmit,
    clearHeadshotError,
    headshotFormError,
    submittingForm,
    submittingHeadshot,
    clearHeadshotFormData,
    headshotUploadError,
    isYP,
  } = props;
  const { onlineApplicationId } = useApplication();
  const { texts: commonTexts } = useTranslations(JoiningPage.Common);
  const { texts } = useTranslations(JoiningPage.AboutYouPage);
  const pageTexts = texts.uploadModal;
  const uploadTexts = texts.uploadModal;

  const UploadImageSchema = React.useMemo(
    () => createUploadImageSchema(pageTexts, commonTexts),
    [pageTexts, commonTexts]
  );
  const onDeleteHeadshot = () => {
    clearHeadshotFormData();
    clearHeadshotError();
  };

  const openUploadModal = () => {
    clearHeadshotError();
    setShowUploadModal(true);
  };

  const photo = initialFormValues.photo;
  const isUrl = typeof photo === "string";
  const isFile = !isUrl;

  return (
    <>
      {isUrl && !submittingHeadshot && (
        <section className="flex justify-between border-b border-t border-dashed border-grey-light-2 bg-color-neutral-three px-2 py-1">
          <div className="flex space-x-2">
            <img
              src={constructWebmediaPhotoUrl(photo, `${onlineApplicationId}`)}
              alt={uploadTexts.uploadedImageAltText}
              className="h-full max-h-36"
              style={{ minHeight: 120 }}
            />
            <div className="flex flex-col space-y-1">
              <span className="mt-2 pr-1">
                {uploadTexts.photographerNameLabel}{" "}
                {initialFormValues.photographerName}
              </span>
            </div>
          </div>
          <ActionButton
            svgIcon={<DeleteIcon />}
            onClick={onDeleteHeadshot}
            size="small"
          />
        </section>
      )}

      <Formik
        initialValues={initialFormValues}
        onSubmit={onSubmit}
        innerRef={ref}
        validationSchema={UploadImageSchema}
        enableReinitialize
      >
        {({ values, setFieldValue, handleSubmit }) => {
          if (!submittingHeadshot && !isFile && !showUploadModal) return null;

          return (
            <>
              <HeadshotDropzone
                setPhoto={(photo) => setFieldValue("photo", photo)}
                showUploadModal={openUploadModal}
                headshotFormError={headshotFormError}
                headshotUploadError={headshotUploadError}
                submittingHeadshot={submittingHeadshot}
                isYP={isYP}
              />
              {!isUrl &&
                !submittingHeadshot &&
                values.photographerName &&
                !showUploadModal && (
                  <section className="flex justify-between border-b border-t border-dashed border-grey-light-2 bg-color-neutral-three px-2 py-1">
                    <div className="flex space-x-2">
                      <div className="flex flex-col space-y-1">
                        <span className="mt-2 pr-1">
                          {uploadTexts.photographerNameLabel}{" "}
                          {values.photographerName}
                        </span>
                      </div>
                    </div>
                    <ActionButton
                      svgIcon={<DeleteIcon />}
                      onClick={onDeleteHeadshot}
                      size="small"
                    />
                  </section>
                )}
              {showUploadModal && values.photo.length > 0 && (
                <UploadImageModal
                  image={values.photo[0] as IDropzoneUploaderFile}
                  onSavePhotoData={handleSubmit}
                  closeModal={() => {
                    onDeleteHeadshot();
                    setShowUploadModal(false);
                  }}
                  disableSubmit={submittingForm}
                  isYP={isYP}
                />
              )}
            </>
          );
        }}
      </Formik>
    </>
  );
});

export default AboutYouFormPart1;
